import {IntlShape, MessageDescriptor, defineMessages} from 'react-intl'

/**
 * Catálogo de Tickets de atención (`/tickets`). Namespace `comms.tickets.*`,
 * con el resto de atención al cliente.
 *
 * **Las etiquetas del modal de vista previa y las de la plantilla de impresión
 * son las mismas.** La plantilla se arma como un template literal de HTML —
 * invisible para el rule, para `scan` y para `nonjsx`—, así que comparte llaves
 * con el modal en vez de duplicarlas: si se traduce una, la otra ya está.
 *
 * No se traducen: el asunto ni las observaciones de un ticket, los nombres de
 * tipo y de grupo, ni la referencia externa — todo eso son datos de BD.
 */
export const TICKET_MESSAGES = defineMessages({
  // ── Página ──
  title: {id: 'MENU.item.tickets', defaultMessage: 'Tickets'},
  newTicket: {id: 'comms.tickets.action.new', defaultMessage: 'New Ticket'},
  quickSearch: {
    id: 'comms.tickets.quickSearch',
    defaultMessage: 'Quick search: ID, subject, reference...',
  },
  empty: {id: 'comms.tickets.empty', defaultMessage: 'No tickets found.'},
  showResolvedClosed: {
    id: 'comms.tickets.showResolvedClosed',
    defaultMessage: 'Show Resolved/Closed',
  },

  // ── Tarjetas de resumen ──
  kpiActive: {id: 'comms.tickets.kpi.active', defaultMessage: 'Active Tickets'},
  kpiActiveMeta: {id: 'comms.tickets.kpi.activeMeta', defaultMessage: 'Open Queue'},
  kpiActiveFoot: {
    id: 'comms.tickets.kpi.activeFoot',
    defaultMessage: 'Excludes resolved and closed tickets',
  },
  kpiUrgent: {id: 'comms.tickets.kpi.urgent', defaultMessage: 'Urgent Tickets'},
  kpiUrgentMeta: {id: 'comms.tickets.kpi.urgentMeta', defaultMessage: 'Priority: Urgent'},
  kpiUrgentFoot: {
    id: 'comms.tickets.kpi.urgentFoot',
    defaultMessage: 'Immediate attention needed',
  },
  kpiOverdue: {id: 'comms.tickets.kpi.overdue', defaultMessage: 'Overdue Tickets'},
  kpiOverdueMeta: {id: 'comms.tickets.kpi.overdueMeta', defaultMessage: 'SLA Breach'},
  kpiOverdueFoot: {id: 'comms.tickets.kpi.overdueFoot', defaultMessage: 'Past due date'},
  kpiToday: {id: 'comms.tickets.kpi.today', defaultMessage: 'Tickets Today'},
  kpiTodayMeta: {id: 'comms.tickets.kpi.todayMeta', defaultMessage: 'Today'},
  kpiTodayFoot: {
    id: 'comms.tickets.kpi.todayFoot',
    defaultMessage: '{count} currently active',
  },
  kpiResolved: {id: 'comms.tickets.kpi.resolved', defaultMessage: 'Resolved Today'},
  kpiResolvedFoot: {
    id: 'comms.tickets.kpi.resolvedFoot',
    defaultMessage: '{count} total solved/closed',
  },

  // ── Tabla ──
  colId: {id: 'comms.tickets.col.id', defaultMessage: 'ID'},
  colSubject: {id: 'common.field.subject', defaultMessage: 'Subject'},
  colDirectedTo: {id: 'comms.tickets.col.directedTo', defaultMessage: 'Directed To'},
  colCreator: {id: 'comms.tickets.col.creator', defaultMessage: 'Creator'},
  colDue: {id: 'comms.tickets.col.due', defaultMessage: 'Due'},
  colClosedResolved: {
    id: 'comms.tickets.col.closedResolved',
    defaultMessage: 'Closed / Resolved',
  },
  noPriority: {id: 'comms.tickets.noPriority', defaultMessage: 'No Priority'},

  // ── Acciones de fila ──
  print: {id: 'comms.tickets.action.print', defaultMessage: 'Print'},
  preparingPrint: {
    id: 'comms.tickets.action.preparingPrint',
    defaultMessage: 'Preparing print...',
  },
  archive: {id: 'admin.organizations.action.archive', defaultMessage: 'Archive'},
  unarchive: {id: 'commissions.reports.action.unarchive', defaultMessage: 'Unarchive'},

  // ── Vista previa y plantilla de impresión (comparten llaves) ──
  previewTitle: {id: 'comms.tickets.preview.title', defaultMessage: 'Ticket Preview'},
  ticketNumber: {id: 'comms.tickets.preview.number', defaultMessage: 'Ticket #{id}'},
  /** Titulo de la ventana de impresion. */
  printTitle: {
    id: 'comms.tickets.print.title',
    defaultMessage: 'Ticket {id} - Print',
  },
  overdue: {id: 'comms.tickets.preview.overdue', defaultMessage: 'Overdue'},
  updated: {id: 'comms.tickets.preview.updated', defaultMessage: 'Updated'},
  lastUpdatedBy: {
    id: 'comms.tickets.preview.lastUpdatedBy',
    defaultMessage: 'Last Updated By',
  },
  externalReference: {
    id: 'comms.tickets.preview.externalReference',
    defaultMessage: 'External Reference',
  },
  firstResponse: {
    id: 'comms.tickets.preview.firstResponse',
    defaultMessage: 'First Response',
  },
  dueDate: {id: 'comms.tickets.preview.dueDate', defaultMessage: 'Due Date'},
  slaBreachedAt: {id: 'comms.tickets.preview.slaBreachedAt', defaultMessage: 'SLA Breached At'},
  observations: {id: 'leads.form.observations', defaultMessage: 'Observations'},
  noObservations: {
    id: 'comms.tickets.preview.noObservations',
    defaultMessage: 'No observations.',
  },
  notesCount: {id: 'comms.tickets.preview.notesCount', defaultMessage: 'Notes ({count})'},
  noNotes: {id: 'comms.tickets.preview.noNotes', defaultMessage: 'No notes registered.'},
  attachmentsCount: {
    id: 'comms.tickets.preview.attachmentsCount',
    defaultMessage: 'Attachments ({count})',
  },
  noAttachments: {
    id: 'comms.tickets.preview.noAttachments',
    defaultMessage: 'No attachments uploaded.',
  },
  uploadedBy: {id: 'comms.tickets.preview.uploadedBy', defaultMessage: 'Uploaded by {name}'},
  generatedAt: {id: 'comms.tickets.preview.generatedAt', defaultMessage: 'Generated {date}'},
  openTicket: {id: 'comms.tickets.preview.openTicket', defaultMessage: 'Open Ticket'},
  loadingPreview: {
    id: 'comms.tickets.preview.loading',
    defaultMessage: 'Loading complete preview details...',
  },

  // ── Filtros guardados ──
  errFilterNameRequired: {
    id: 'comms.tickets.filters.error.nameRequired',
    defaultMessage: 'Filter name is required.',
  },
  toastFilterSaved: {
    id: 'comms.tickets.filters.toast.saved',
    defaultMessage: 'Filter saved.',
  },
  toastFilterUpdated: {
    id: 'comms.tickets.filters.toast.updated',
    defaultMessage: 'Filter updated.',
  },
  toastFilterDeleted: {
    id: 'comms.tickets.filters.toast.deleted',
    defaultMessage: 'Filter deleted.',
  },

  // ── Modal de filtros ──
  filtersTitle: {id: 'comms.tickets.filters.title', defaultMessage: 'Advanced Ticket Filters'},
  tabFilters: {id: 'comms.tickets.filters.tab.filters', defaultMessage: 'Filters'},
  tabSaved: {id: 'comms.tickets.filters.tab.saved', defaultMessage: 'Saved filters'},
  saveFilter: {id: 'comms.tickets.filters.save', defaultMessage: 'Save Filter'},
  applyFilters: {id: 'comms.tickets.filters.apply', defaultMessage: 'Apply Filters'},
  savedFilter: {id: 'comms.tickets.filters.savedFilter', defaultMessage: 'Saved filter'},
  selectSavedFilter: {
    id: 'comms.tickets.filters.selectSaved',
    defaultMessage: 'Select saved filter',
  },
  filterName: {id: 'comms.tickets.filters.name', defaultMessage: 'Filter name'},
  backToFilters: {id: 'comms.tickets.filters.back', defaultMessage: 'Back to filters'},
  saveAsNew: {id: 'comms.tickets.filters.saveAsNew', defaultMessage: 'Save as new'},
  applySelected: {
    id: 'comms.tickets.filters.applySelected',
    defaultMessage: 'Apply selected',
  },
  searchText: {id: 'comms.tickets.filters.searchText', defaultMessage: 'Search text'},
  allChannels: {id: 'comms.field.allChannels', defaultMessage: 'All channels'},
  priority: {id: 'comms.tickets.filters.priority', defaultMessage: 'Priority'},
  ticketType: {id: 'comms.tickets.filters.ticketType', defaultMessage: 'Ticket type'},
  assignmentType: {
    id: 'comms.tickets.filters.assignmentType',
    defaultMessage: 'Assignment type',
  },
  allAssignments: {
    id: 'comms.tickets.filters.allAssignments',
    defaultMessage: 'All assignments',
  },
  inboxMode: {id: 'comms.tickets.filters.inboxMode', defaultMessage: 'Inbox mode'},
  inboxMy: {id: 'comms.tickets.filters.inbox.my', defaultMessage: 'My queue'},
  inboxGroup: {id: 'comms.tickets.filters.inbox.group', defaultMessage: 'Group queue'},
  inboxUnassigned: {
    id: 'comms.tickets.filters.inbox.unassigned',
    defaultMessage: 'Unassigned',
  },
  assignedUser: {id: 'comms.tickets.filters.assignedUser', defaultMessage: 'Assigned user'},
  assignedGroup: {id: 'comms.tickets.filters.assignedGroup', defaultMessage: 'Assigned group'},
  createdBy: {id: 'comms.tickets.filters.createdBy', defaultMessage: 'Created by'},
  createdFrom: {
    id: 'comms.tickets.filters.createdFrom',
    defaultMessage: 'Created date from',
  },
  createdTo: {id: 'comms.tickets.filters.createdTo', defaultMessage: 'Created date to'},
  dueFrom: {id: 'comms.tickets.filters.dueFrom', defaultMessage: 'Due date from'},
  dueTo: {id: 'comms.tickets.filters.dueTo', defaultMessage: 'Due date to'},

  // ── Marcadores de posición de los multiselect ──
  allStatus: {id: 'comms.tickets.filters.allStatus', defaultMessage: 'All status'},
  allPriorities: {
    id: 'comms.tickets.filters.allPriorities',
    defaultMessage: 'All priorities',
  },
  allTypes: {id: 'comms.tickets.filters.allTypes', defaultMessage: 'All types'},
  allUsers: {id: 'comms.tickets.filters.allUsers', defaultMessage: 'All users'},
  allGroups: {id: 'comms.tickets.filters.allGroups', defaultMessage: 'All groups'},
  allCreators: {id: 'comms.tickets.filters.allCreators', defaultMessage: 'All creators'},
  enterFilterName: {
    id: 'comms.tickets.filters.enterName',
    defaultMessage: 'Enter filter name',
  },

  // ── Casillas del modal y conmutadores del listado ──
  pendingInbox: {id: 'comms.tickets.filters.pendingInbox', defaultMessage: 'Pending inbox'},
  onlyOverdue: {id: 'comms.tickets.filters.onlyOverdue', defaultMessage: 'Only overdue'},
  onlyUnassigned: {
    id: 'comms.tickets.filters.onlyUnassigned',
    defaultMessage: 'Only unassigned',
  },
  showAssignedToMe: {
    id: 'comms.tickets.showAssignedToMe',
    defaultMessage: 'Show only Assigned to Me',
  },
  showCreatedByMe: {
    id: 'comms.tickets.showCreatedByMe',
    defaultMessage: 'Show created by me',
  },

  // ── Etiquetas de los chips de filtros aplicados ──
  chipCreatedDate: {id: 'comms.tickets.chip.createdDate', defaultMessage: 'Created Date'},
  chipAssignedUser: {id: 'comms.tickets.chip.assignedUser', defaultMessage: 'Assigned User'},
  chipAssignedGroup: {
    id: 'comms.tickets.chip.assignedGroup',
    defaultMessage: 'Assigned Group',
  },

  // ── Menú lateral de secciones ──
  sectionSubDetails: {
    id: 'comms.tickets.form.section.detailsSub',
    defaultMessage: 'General and assignment',
  },
  sectionSubNotes: {
    id: 'comms.tickets.form.section.notesSub',
    defaultMessage: 'Internal comments',
  },
  sectionSubAttachments: {
    id: 'comms.tickets.form.section.attachmentsSub',
    defaultMessage: 'Files and evidence',
  },
  sectionSubHistory: {
    id: 'comms.tickets.form.section.historySub',
    defaultMessage: 'Status transitions',
  },
  sectionSubAudit: {
    id: 'comms.tickets.form.section.auditSub',
    defaultMessage: 'Changed fields',
  },

  /**
   * Avisos de sección no disponible. Se pasan **como argumento** a
   * `sectionUnavailable()`, un helper local: ni el rule ni el paso 6 del gate
   * los ven (guía §17.bis, trampa 1).
   */
  unavailableAttachments: {
    id: 'comms.tickets.form.unavailable.attachments',
    defaultMessage: 'Create the ticket first to upload attachments.',
  },
  unavailableNotes: {
    id: 'comms.tickets.form.unavailable.notes',
    defaultMessage: 'Create the ticket first to add notes.',
  },
  unavailableHistory: {
    id: 'comms.tickets.form.unavailable.history',
    defaultMessage: 'Create the ticket first to view status history.',
  },
  unavailableAudit: {
    id: 'comms.tickets.form.unavailable.audit',
    defaultMessage: 'Create the ticket first to view audit logs.',
  },
  auditAdminOnly: {
    id: 'comms.tickets.form.unavailable.auditAdminOnly',
    defaultMessage: 'Audit log is available only for administrators.',
  },

  /**
   * Barra de contexto del área de trabajo. En inglés va en mayúsculas por
   * estilo; en español no (misma regla que las cabeceras de columna).
   */
  workspaceTicket: {
    id: 'comms.tickets.form.workspace.ticket',
    defaultMessage: 'TICKET #{id} WORKSPACE',
  },
  workspaceNew: {
    id: 'comms.tickets.form.workspace.new',
    defaultMessage: 'NEW TICKET WORKSPACE',
  },
  workspaceNewPolicy: {
    id: 'comms.tickets.form.workspace.newPolicy',
    defaultMessage: 'NEW TICKET WORKSPACE - POLICY: {policy} - EFFECTIVE DATE: {date}',
  },
  previewPolicy: {
    id: 'comms.tickets.form.previewPolicy',
    defaultMessage: 'Preview Policy',
  },
  previewPolicyOf: {
    id: 'comms.tickets.form.previewPolicyOf',
    defaultMessage: 'Preview Policy of {name}',
  },
  viewPolicy: {id: 'commissions.logs.action.viewPolicy', defaultMessage: 'View Policy'},

  // ── Selector de usuario asignado ──
  selectUser: {id: 'leads.kanban.selectUser', defaultMessage: 'Select user'},
  noName: {id: 'comms.tickets.form.noName', defaultMessage: 'No name'},
  noEmail: {id: 'comms.tickets.form.noEmail', defaultMessage: 'No email'},
  noUsersFound: {id: 'comms.tickets.form.noUsersFound', defaultMessage: 'No users found'},
  noUsersAvailable: {
    id: 'comms.tickets.form.noUsersAvailable',
    defaultMessage: 'No users available',
  },

  // ── Valores calculados de la tabla de auditoría ──
  notAvailable: {id: 'common.value.notAvailable', defaultMessage: 'N/A'},
  unknownField: {id: 'comms.tickets.form.unknownField', defaultMessage: 'Unknown field'},
  emptyValue: {id: 'comms.tickets.form.emptyValue', defaultMessage: 'Empty'},

  // ── Formulario del ticket ──
  formDataTitle: {id: 'comms.tickets.form.dataTitle', defaultMessage: 'Ticket Data'},
  formDataHint: {
    id: 'comms.tickets.form.dataHint',
    defaultMessage: 'Complete the general ticket information and assignment.',
  },
  badgeGroup: {id: 'comms.tickets.form.badgeGroup', defaultMessage: 'Group: {name}'},
  badgeUser: {id: 'comms.tickets.form.badgeUser', defaultMessage: 'User: {name}'},
  assignToMe: {id: 'comms.tickets.form.assignToMe', defaultMessage: 'Assign to me'},
  /** El asterisco de campo obligatorio va fuera del texto traducido. */
  subject: {id: 'common.field.subject', defaultMessage: 'Subject'},
  assignment: {id: 'leads.kanban.thAssignment', defaultMessage: 'Assignment'},
  selectType: {id: 'admin.deductionTypes.selectType', defaultMessage: 'Select type'},
  selectGroup: {id: 'comms.tickets.form.selectGroup', defaultMessage: 'Select group'},
  unassigned: {id: 'leads.kanban.unassigned', defaultMessage: 'Unassigned'},
  reassignHint: {
    id: 'comms.tickets.form.reassignHint',
    defaultMessage:
      'Reassign to another user from this selector. Only users from the same assigned group are allowed.',
  },
  externalReferencePlaceholder: {
    id: 'comms.tickets.form.externalReferencePlaceholder',
    defaultMessage: 'Optional external ID from channel/provider',
  },
  dueDateSla: {id: 'comms.tickets.form.dueDateSla', defaultMessage: 'Due Date (SLA)'},

  // ── Adjuntos y notas ──
  attachments: {id: 'leads.kanban.attachments', defaultMessage: 'Attachments'},
  attachmentsHint: {
    id: 'comms.tickets.form.attachmentsHint',
    defaultMessage: 'Upload supporting files and manage current attachments.',
  },
  noAttachmentsYet: {
    id: 'comms.tickets.form.noAttachmentsYet',
    defaultMessage: 'No attachments yet.',
  },
  notes: {id: 'policies.detail.section.notes', defaultMessage: 'Notes'},
  notesHint: {
    id: 'comms.tickets.form.notesHint',
    defaultMessage: 'Track internal communication related to this ticket.',
  },
  noNotesYet: {id: 'comms.tickets.form.noNotesYet', defaultMessage: 'No notes yet.'},
  writeComment: {id: 'comms.tickets.form.writeComment', defaultMessage: 'Write a comment...'},

  // ── Historial de estados ──
  statusHistory: {id: 'comms.tickets.form.statusHistory', defaultMessage: 'Status History'},
  statusHistoryHint: {
    id: 'comms.tickets.form.statusHistoryHint',
    defaultMessage: 'Detailed timeline of transitions, owners, and context comments.',
  },
  totalTransitions: {
    id: 'comms.tickets.form.totalTransitions',
    defaultMessage: 'Total transitions',
  },
  withComments: {id: 'comms.tickets.form.withComments', defaultMessage: 'With comments'},
  waitingCustomerCount: {
    id: 'comms.tickets.form.waitingCustomer',
    defaultMessage: 'Waiting customer',
  },
  reopenEvents: {id: 'comms.tickets.form.reopenEvents', defaultMessage: 'Reopen events'},
  latestTransitionBy: {
    id: 'comms.tickets.form.latestTransitionBy',
    defaultMessage: 'Latest transition by <b>{name}</b> on {date}',
  },
  changedBy: {id: 'comms.tickets.form.changedBy', defaultMessage: 'Changed by:'},
  commentLabel: {id: 'comms.tickets.form.commentLabel', defaultMessage: 'Comment:'},
  noComment: {id: 'comms.tickets.form.noComment', defaultMessage: 'No comment'},
  noStatusHistory: {
    id: 'comms.tickets.form.noStatusHistory',
    defaultMessage: 'No status history yet.',
  },

  // ── Registro de auditoría ──
  auditLog: {id: 'comms.tickets.form.auditLog', defaultMessage: 'Audit Log'},
  auditLogHint: {
    id: 'comms.tickets.form.auditLogHint',
    defaultMessage: 'Detailed field-level change tracking with before and after values.',
  },
  totalChanges: {id: 'comms.tickets.form.totalChanges', defaultMessage: 'Total changes'},
  fieldsImpacted: {id: 'comms.tickets.form.fieldsImpacted', defaultMessage: 'Fields impacted'},
  usersInvolved: {id: 'comms.tickets.form.usersInvolved', defaultMessage: 'Users involved'},
  latestChangeBy: {
    id: 'comms.tickets.form.latestChangeBy',
    defaultMessage: 'Latest change by <b>{name}</b> on {date}',
  },
  previousValue: {id: 'comms.tickets.form.previousValue', defaultMessage: 'Previous value'},
  newValue: {id: 'comms.tickets.form.newValue', defaultMessage: 'New value'},
  noAuditLog: {id: 'comms.tickets.form.noAuditLog', defaultMessage: 'No audit log yet.'},

  // ── Asistente de creación ──
  createTitle: {id: 'comms.tickets.form.createTitle', defaultMessage: 'Create a new ticket'},
  editTitle: {id: 'comms.tickets.form.editTitle', defaultMessage: 'Edit ticket #{id}'},
  optional: {id: 'leads.kanban.optional', defaultMessage: 'Optional'},
  attachmentsWizardHint: {
    id: 'comms.tickets.form.attachmentsWizardHint',
    defaultMessage:
      'Drag & drop files, paste from clipboard (Ctrl+V / Cmd+V), or click to select. You can skip this step.',
  },
  dropFilesHere: {id: 'comms.tickets.form.dropFilesHere', defaultMessage: 'Drop files here'},
  dragOrPaste: {
    id: 'comms.tickets.form.dragOrPaste',
    defaultMessage: 'Drag & drop or paste from clipboard',
  },
  notesWizardHint: {
    id: 'comms.tickets.form.notesWizardHint',
    defaultMessage: 'Add an initial internal note for this ticket. You can skip this step.',
  },
  notesAdded: {
    id: 'comms.tickets.form.notesAdded',
    defaultMessage: '{count, plural, one {# note added} other {# notes added}}',
  },
  createAndContinue: {
    id: 'comms.tickets.form.createAndContinue',
    defaultMessage: 'Create & Continue',
  },
  finish: {id: 'comms.tickets.form.finish', defaultMessage: 'Finish'},

  // ── Toasts del formulario ──
  warnSubjectRequired: {
    id: 'comms.tickets.form.warn.subject',
    defaultMessage: 'Subject is required',
  },
  warnUserRequired: {
    id: 'comms.tickets.form.warn.assignedUser',
    defaultMessage: 'Assigned user is required',
  },
  warnGroupRequired: {
    id: 'comms.tickets.form.warn.assignedGroup',
    defaultMessage: 'Assigned group is required',
  },
  toastCreated: {id: 'comms.tickets.form.toast.created', defaultMessage: 'Ticket created'},
  toastUpdated: {id: 'comms.tickets.form.toast.updated', defaultMessage: 'Ticket updated'},
  toastNoteSaved: {id: 'comms.tickets.form.toast.noteSaved', defaultMessage: 'Note saved'},
  toastAttachmentDeleted: {
    id: 'comms.tickets.form.toast.attachmentDeleted',
    defaultMessage: 'Attachment deleted',
  },
  toastAssignedToYou: {
    id: 'comms.tickets.form.toast.assignedToYou',
    defaultMessage: 'Ticket assigned to you',
  },
  infoUploading: {
    id: 'comms.tickets.form.info.uploading',
    defaultMessage: 'Uploading files...',
  },
  errNoPermissionCreate: {
    id: 'comms.tickets.form.error.noPermissionCreate',
    defaultMessage: 'No permission to create tickets',
  },
  errNoPermissionUpdate: {
    id: 'comms.tickets.form.error.noPermissionUpdate',
    defaultMessage: 'No permission to update this ticket',
  },
  errNoPermissionComment: {
    id: 'comms.tickets.form.error.noPermissionComment',
    defaultMessage: 'No permission to comment on tickets',
  },
  errNoPermissionUpload: {
    id: 'comms.tickets.form.error.noPermissionUpload',
    defaultMessage: 'No permission to upload attachments',
  },
  errLoadingTicket: {
    id: 'comms.tickets.form.error.loadingTicket',
    defaultMessage: 'Error loading ticket: {message}',
  },
  /**
   * Los cuatro siguientes se lanzan con `new Error` y **si** llegan al usuario:
   * el `catch` de cada handler pinta su `.message` en un toast.
   */
  errCreate: {
    id: 'comms.tickets.form.error.create',
    defaultMessage: 'Could not create ticket',
  },
  errUpdate: {
    id: 'comms.tickets.form.error.update',
    defaultMessage: 'Could not update ticket',
  },
  errSaveNote: {
    id: 'comms.tickets.form.error.saveNote',
    defaultMessage: 'Could not save note',
  },
  errDeleteAttachment: {
    id: 'comms.tickets.form.error.deleteAttachment',
    defaultMessage: 'Could not delete attachment',
  },
  errAssignToMe: {
    id: 'comms.tickets.form.error.assignToMe',
    defaultMessage: 'Could not assign ticket to current user',
  },
  confirmDeleteSavedFilter: {
    id: 'comms.tickets.filters.confirmDelete',
    defaultMessage: 'Delete this saved filter?',
  },

  // ── Toasts y confirmaciones ──
  errLoading: {
    id: 'comms.tickets.error.loading',
    defaultMessage: 'Error loading tickets: {message}',
  },
  errLoadingDashboard: {
    id: 'comms.tickets.error.loadingDashboard',
    defaultMessage: 'Error loading ticket dashboard: {message}',
  },
  errLoadingMetrics: {
    id: 'comms.tickets.error.loadingMetrics',
    defaultMessage: 'Error loading ticket metrics: {message}',
  },
  confirmDelete: {
    id: 'comms.tickets.confirm.delete',
    defaultMessage: 'Delete ticket #{id}?',
  },
  toastDeleted: {id: 'comms.tickets.toast.deleted', defaultMessage: 'Ticket deleted'},
  errDelete: {id: 'comms.tickets.error.delete', defaultMessage: 'Could not delete ticket'},
  /**
   * Frase completa por variante: en español no basta con sustituir el verbo
   * («¿Quiere archivar…?» / «¿Quiere desarchivar…?»).
   */
  confirmArchive: {
    id: 'comms.tickets.confirm.archive',
    defaultMessage: 'Do you want to archive ticket {id}?',
  },
  confirmUnarchive: {
    id: 'comms.tickets.confirm.unarchive',
    defaultMessage: 'Do you want to unarchive ticket {id}?',
  },
  toastArchived: {id: 'comms.tickets.toast.archived', defaultMessage: 'Ticket archived'},
  toastUnarchived: {id: 'comms.tickets.toast.unarchived', defaultMessage: 'Ticket unarchived'},
  errArchive: {
    id: 'comms.tickets.error.archive',
    defaultMessage: 'Could not update archive status',
  },
  errPrint: {
    id: 'comms.tickets.error.print',
    defaultMessage: 'Error preparing print: {message}',
  },
  errPopups: {
    id: 'comms.tickets.error.popups',
    defaultMessage: 'Please allow pop-ups to print this preview.',
  },

  // ── Reusadas ──
  status: {id: 'common.field.status', defaultMessage: 'Status'},
  type: {id: 'common.field.type', defaultMessage: 'Type'},
  channel: {id: 'comms.field.channel', defaultMessage: 'Channel'},
  actions: {id: 'common.field.actions', defaultMessage: 'Actions'},
  edit: {id: 'common.action.edit', defaultMessage: 'Edit'},
  close: {id: 'common.action.close', defaultMessage: 'Close'},
  preview: {id: 'common.action.preview', defaultMessage: 'Preview'},
  search: {id: 'common.action.search', defaultMessage: 'Search'},
  filter: {id: 'common.action.filter', defaultMessage: 'Filter'},
  delete: {id: 'common.action.delete', defaultMessage: 'Delete'},
  errorWithMessage: {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  update: {id: 'common.action.update', defaultMessage: 'Update'},
  all: {id: 'common.pagination.all', defaultMessage: 'All'},
  user: {id: 'common.field.user', defaultMessage: 'User'},
  group: {id: 'common.field.group', defaultMessage: 'Group'},
  yes: {id: 'common.boolean.yes', defaultMessage: 'Yes'},
  no: {id: 'common.boolean.no', defaultMessage: 'No'},
  view: {id: 'common.action.view', defaultMessage: 'View'},
  addNote: {id: 'policies.list.page.act.addNote', defaultMessage: 'Add Note'},
  saveChanges: {id: 'common.action.saveChanges', defaultMessage: 'Save changes'},
  next: {id: 'common.action.next', defaultMessage: 'Next'},
  previous: {id: 'common.action.previous', defaultMessage: 'Previous'},
  file: {id: 'contacts.profile.documents.col.file', defaultMessage: 'File'},
  by: {id: 'policies.list.carrierStatus.label.by', defaultMessage: 'By'},
  created: {id: 'common.field.created', defaultMessage: 'Created'},
  field: {id: 'leads.activity.field', defaultMessage: 'Field'},
  comment: {id: 'common.field.comment', defaultMessage: 'Comment'},
  date: {id: 'common.field.date', defaultMessage: 'Date'},
  uploaded: {id: 'policies.list.carrierStatus.label.uploaded', defaultMessage: 'Uploaded'},
  uploading: {id: 'leads.kanban.uploading', defaultMessage: 'Uploading...'},
})

/** Marcador de celda vacía. Símbolo, no texto. */
export const DASH = '-'
/** Marca de campo obligatorio. Simbolo, no texto. */
export const REQUIRED_MARK = ' *'
/** Prefijo de numero de fila. Simbolo. */
export const HASH = '#'

type Labels = Record<string, MessageDescriptor>

/**
 * Estado del ticket. Antes se pintaba con `enumLabel`, que solo cambiaba los
 * guiones bajos por espacios: `IN_PROGRESS` salía como «IN PROGRESS».
 */
const STATUS: Labels = defineMessages({
  INITIAL: {id: 'comms.tickets.status.initial', defaultMessage: 'Initial'},
  OPEN: {id: 'policies.detail.tab.tickets.open', defaultMessage: 'Open'},
  IN_PROGRESS: {id: 'comms.tickets.status.inProgress', defaultMessage: 'In Progress'},
  WAITING_CUSTOMER: {
    id: 'comms.tickets.status.waitingCustomer',
    defaultMessage: 'Waiting Customer',
  },
  RESOLVED: {id: 'comms.tickets.status.resolved', defaultMessage: 'Resolved'},
  CLOSED: {id: 'comms.tickets.status.closed', defaultMessage: 'Closed'},
}) as Labels

/** Prioridad del ticket. */
const PRIORITY: Labels = defineMessages({
  URGENT: {id: 'leads.kanban.priorityUrgent', defaultMessage: 'Urgent'},
  HIGH: {id: 'leads.kanban.priorityHigh', defaultMessage: 'High'},
  MEDIUM: {id: 'comms.tickets.priority.medium', defaultMessage: 'Medium'},
  LOW: {id: 'leads.kanban.priorityLow', defaultMessage: 'Low'},
}) as Labels

/**
 * Canal de entrada. `WhatsApp`, `SMS` y `API` son marcas o siglas: se escriben
 * igual en los dos idiomas, así que no llevan llave.
 */
const CHANNEL: Labels = defineMessages({
  INTERNAL: {id: 'comms.tickets.channel.internal', defaultMessage: 'Internal'},
  PHONE: {id: 'common.field.phone', defaultMessage: 'Phone'},
  EMAIL: {id: 'common.field.email', defaultMessage: 'Email'},
  WEB: {id: 'comms.templates.icon.web', defaultMessage: 'Web'},
}) as Labels

const label = (intl: IntlShape, map: Labels, value?: string | null) => {
  if (!value) return DASH
  return map[value] ? intl.formatMessage(map[value]) : value.replace(/_/g, ' ')
}

export const ticketStatusLabel = (intl: IntlShape, value?: string | null) =>
  label(intl, STATUS, value)
export const ticketPriorityLabel = (intl: IntlShape, value?: string | null) =>
  label(intl, PRIORITY, value)
/** `WHATSAPP`, `SMS` y `API` caen al valor crudo, que ya es su nombre. */
export const ticketChannelLabel = (intl: IntlShape, value?: string | null) => {
  if (value === 'WHATSAPP') return 'WhatsApp'
  if (value === 'SMS' || value === 'API') return value
  return label(intl, CHANNEL, value)
}
